import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@/api/client";
import { api } from "@/api/endpoints";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import type {
  AdminDailyRequestPoint,
  AdminDailySimilarityPoint,
  AdminOverviewResponse,
  AdminQueryFilters,
  AdminQueryInfo,
  AdminSimilaritySummary,
  AdminUserInfo,
} from "@/api/types";
import { useSession } from "@/store/session";

type AdminTab = "overview" | "users" | "queries" | "analytics" | "exports";

const pageSize = 25;
const sourceKeys: Array<keyof AdminSimilaritySummary> = ["quran", "messenger", "scholarly", "faq"];

export function Admin() {
  const navigate = useNavigate();
  const email = useSession((s) => s.email);
  const clearSession = useSession((s) => s.clear);

  const [tab, setTab] = useState<AdminTab>("overview");
  const [overview, setOverview] = useState<AdminOverviewResponse | null>(null);
  const [users, setUsers] = useState<AdminUserInfo[]>([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [queries, setQueries] = useState<AdminQueryInfo[]>([]);
  const [queryTotal, setQueryTotal] = useState(0);
  const [queryPage, setQueryPage] = useState(1);
  const [selectedQuery, setSelectedQuery] = useState<AdminQueryInfo | null>(null);
  const [requests, setRequests] = useState<AdminDailyRequestPoint[]>([]);
  const [similarities, setSimilarities] = useState<AdminDailySimilarityPoint[]>([]);
  const [filters, setFilters] = useState({
    since: dateInputDaysAgo(30),
    until: "",
    user: "",
    audience: "all",
    search: "",
    lowSimilarityThreshold: "",
  });
  const [activeFilters, setActiveFilters] = useState<AdminQueryFilters>(() => buildFilters(filters));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    setOverview(await api.admin.overview());
  }, []);

  const loadUsers = useCallback(async () => {
    const result = await api.admin.users({ page: userPage, page_size: pageSize, search: userSearch });
    setUsers(result.users ?? []);
    setUserTotal(result.total ?? 0);
  }, [userPage, userSearch]);

  const loadQueries = useCallback(async () => {
    const result = await api.admin.queries({
      page: queryPage,
      page_size: pageSize,
      filters: activeFilters,
    });
    setQueries(result.queries ?? []);
    setQueryTotal(result.total ?? 0);
  }, [activeFilters, queryPage]);

  const loadAnalytics = useCallback(async () => {
    const range = {
      since: activeFilters.since,
      until: activeFilters.until,
    };
    const [dailyRequests, dailySimilarities] = await Promise.all([
      api.admin.dailyRequests(range),
      api.admin.dailySimilarities(range),
    ]);
    setRequests(dailyRequests.points ?? []);
    setSimilarities(dailySimilarities.points ?? []);
  }, [activeFilters.since, activeFilters.until]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([loadOverview(), loadUsers(), loadQueries(), loadAnalytics()])
      .catch((e) => {
        if (cancelled) return;
        setError(adminErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadAnalytics, loadOverview, loadQueries, loadUsers]);

  const queryPages = Math.max(1, Math.ceil(queryTotal / pageSize));
  const userPages = Math.max(1, Math.ceil(userTotal / pageSize));

  const averageSources = overview?.avg_similarity ?? emptySimilarity();
  const topSource = useMemo(() => {
    return sourceKeys.reduce((best, key) => (averageSources[key] > averageSources[best] ? key : best), "quran");
  }, [averageSources]);

  async function applyFilters() {
    setQueryPage(1);
    setSelectedQuery(null);
    setActiveFilters(buildFilters(filters));
  }

  async function exportQueries() {
    setExporting(true);
    setExportMessage(null);
    try {
      const result = await api.admin.exportQueries(buildFilters(filters));
      downloadText(result.filename, result.content_type, result.csv);
      setExportMessage(`Exported ${formatNumber(result.row_count)} rows.`);
      await loadOverview();
    } catch (e) {
      setExportMessage(adminErrorMessage(e));
    } finally {
      setExporting(false);
    }
  }

  if (error && !overview) {
    return (
      <main className="sa-admin-page">
        <AdminHeader email={email} onBack={() => navigate("/chat")} onSignOut={() => signOut(clearSession, navigate)} />
        <section className="sa-admin-empty">
          <p className="sa-label">Admin</p>
          <h1>Access unavailable</h1>
          <p>{error}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="sa-admin-page">
      <AdminHeader email={email} onBack={() => navigate("/chat")} onSignOut={() => signOut(clearSession, navigate)} />

      <section className="sa-admin-shell">
        <div className="sa-admin-titlebar">
          <div>
            <p className="sa-label">SubmitterAI Admin</p>
            <h1>Product Console</h1>
          </div>
          <div className="sa-admin-status sa-sans">
            {loading ? "Loading" : `${formatNumber(overview?.total_queries ?? 0)} total queries`}
          </div>
        </div>

        <nav className="sa-admin-tabs" aria-label="Admin sections">
          {(["overview", "users", "queries", "analytics", "exports"] as AdminTab[]).map((item) => (
            <button
              key={item}
              type="button"
              className={item === tab ? "is-active" : ""}
              onClick={() => setTab(item)}
            >
              {tabLabel(item)}
            </button>
          ))}
        </nav>

        {error ? <div className="sa-admin-alert sa-sans">{error}</div> : null}

        {tab === "overview" && overview ? (
          <section className="sa-admin-section">
            <div className="sa-admin-metrics">
              <Metric label="Users" value={overview.total_users} />
              <Metric label="Guest Threads" value={overview.total_guest_conversations} />
              <Metric label="Queries" value={overview.total_queries} />
              <Metric label="Last 24h" value={overview.queries_last_24h} />
              <Metric label="Low Similarity" value={overview.low_similarity_queries} />
              <Metric label="Top Source" value={sourceLabel(topSource)} />
            </div>
            <SimilarityGrid summary={averageSources} />
          </section>
        ) : null}

        {tab === "users" ? (
          <section className="sa-admin-section">
            <div className="sa-admin-toolbar">
              <input
                className="sa-input"
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setUserPage(1);
                }}
                placeholder="Search by email or user ID"
              />
            </div>
            <div className="sa-admin-table-wrap">
              <table className="sa-admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Conversations</th>
                    <th>Queries</th>
                    <th>Last Activity</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div>{user.email}</div>
                        <span>{user.id}</span>
                      </td>
                      <td>{user.is_admin ? "Admin" : "User"}</td>
                      <td>{formatNumber(user.conversation_count)}</td>
                      <td>{formatNumber(user.query_count)}</td>
                      <td>{formatUnix(user.last_activity_at)}</td>
                      <td>{formatUnix(user.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={userPage} pages={userPages} total={userTotal} onPage={setUserPage} />
          </section>
        ) : null}

        {tab === "queries" ? (
          <section className="sa-admin-section">
            <QueryFilters filters={filters} setFilters={setFilters} onApply={applyFilters} />
            <div className="sa-admin-table-wrap">
              <table className="sa-admin-table sa-admin-table--queries">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>User</th>
                    <th>Question</th>
                    <th>Response</th>
                    <th>Similarity</th>
                    <th>Sources</th>
                  </tr>
                </thead>
                <tbody>
                  {queries.map((query) => (
                    <tr key={query.response_message_id} onClick={() => setSelectedQuery(query)}>
                      <td>{formatUnix(query.created_at)}</td>
                      <td>{query.is_guest ? "Guest" : query.user_email || query.user_id}</td>
                      <td>{truncate(query.question, 96)}</td>
                      <td>{truncate(query.answer, 120)}</td>
                      <td><MiniSimilarity summary={query.avg_similarity} /></td>
                      <td>{query.sources.length ? query.sources.length : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={queryPage} pages={queryPages} total={queryTotal} onPage={setQueryPage} />
          </section>
        ) : null}

        {tab === "analytics" ? (
          <section className="sa-admin-section sa-admin-grid-two">
            <div className="sa-admin-chart-panel">
              <h2>Daily Requests</h2>
              <DailyRequestChart points={requests} />
            </div>
            <div className="sa-admin-chart-panel">
              <h2>Average Similarity By Source</h2>
              <SimilarityLineChart points={similarities} />
            </div>
          </section>
        ) : null}

        {tab === "exports" && overview ? (
          <section className="sa-admin-section">
            <QueryFilters filters={filters} setFilters={setFilters} onApply={applyFilters} compact />
            <div className="sa-admin-export-row">
              <button className="sa-btn" type="button" onClick={exportQueries} disabled={exporting}>
                {exporting ? "Exporting" : "Export Queries CSV"}
              </button>
              {exportMessage ? <span className="sa-sans">{exportMessage}</span> : null}
            </div>
            <div className="sa-admin-table-wrap">
              <table className="sa-admin-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Action</th>
                    <th>Rows</th>
                    <th>Filters</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.recent_exports.map((event) => (
                    <tr key={event.id}>
                      <td>{formatUnix(event.created_at)}</td>
                      <td>{event.action}</td>
                      <td>{formatNumber(event.row_count)}</td>
                      <td>{truncate(event.filters_json, 160)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </section>

      {selectedQuery ? (
        <aside className="sa-admin-detail" role="dialog" aria-modal="true">
          <button type="button" className="sa-admin-detail__close" onClick={() => setSelectedQuery(null)}>
            Close
          </button>
          <p className="sa-label">{selectedQuery.is_guest ? "Guest Query" : selectedQuery.user_email}</p>
          <h2>{formatUnix(selectedQuery.created_at)}</h2>
          <SimilarityGrid summary={selectedQuery.avg_similarity ?? emptySimilarity()} compact />
          <h3>Question</h3>
          <p>{selectedQuery.question}</p>
          <h3>Response</h3>
          <p>{selectedQuery.answer}</p>
          <h3>Sources</h3>
          <p className="sa-sans">{selectedQuery.sources.length ? selectedQuery.sources.join(", ") : "None"}</p>
        </aside>
      ) : null}
    </main>
  );
}

function AdminHeader({
  email,
  onBack,
  onSignOut,
}: {
  email: string | null;
  onBack: () => void;
  onSignOut: () => void;
}) {
  return (
    <header className="sa-admin-header">
      <ThemeToggle />
      <button type="button" className="sa-btn sa-btn--ghost" onClick={onBack}>Chat</button>
      <span className="sa-sans">{email}</span>
      <button type="button" className="sa-btn sa-btn--link" onClick={onSignOut}>Sign out</button>
    </header>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="sa-admin-metric">
      <span>{label}</span>
      <strong>{typeof value === "number" ? formatNumber(value) : value}</strong>
    </div>
  );
}

function QueryFilters({
  filters,
  setFilters,
  onApply,
  compact = false,
}: {
  filters: {
    since: string;
    until: string;
    user: string;
    audience: string;
    search: string;
    lowSimilarityThreshold: string;
  };
  setFilters: (next: {
    since: string;
    until: string;
    user: string;
    audience: string;
    search: string;
    lowSimilarityThreshold: string;
  }) => void;
  onApply: () => void;
  compact?: boolean;
}) {
  return (
    <div className={compact ? "sa-admin-filters is-compact" : "sa-admin-filters"}>
      <input className="sa-input" type="date" value={filters.since} onChange={(e) => setFilters({ ...filters, since: e.target.value })} />
      <input className="sa-input" type="date" value={filters.until} onChange={(e) => setFilters({ ...filters, until: e.target.value })} />
      <select className="sa-input" value={filters.audience} onChange={(e) => setFilters({ ...filters, audience: e.target.value })}>
        <option value="all">All</option>
        <option value="registered">Registered</option>
        <option value="guest">Guest</option>
      </select>
      <input className="sa-input" value={filters.user} placeholder="User email or ID" onChange={(e) => setFilters({ ...filters, user: e.target.value })} />
      <input className="sa-input" value={filters.search} placeholder="Question or response text" onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
      <input className="sa-input" value={filters.lowSimilarityThreshold} placeholder="Low similarity <= 0.40" onChange={(e) => setFilters({ ...filters, lowSimilarityThreshold: e.target.value })} />
      <button className="sa-btn" type="button" onClick={onApply}>Apply</button>
    </div>
  );
}

function SimilarityGrid({ summary, compact = false }: { summary: AdminSimilaritySummary; compact?: boolean }) {
  return (
    <div className={compact ? "sa-admin-sim-grid is-compact" : "sa-admin-sim-grid"}>
      {sourceKeys.map((key) => (
        <div key={key}>
          <span>{sourceLabel(key)}</span>
          <strong>{formatSimilarity(summary[key])}</strong>
          <div><i style={{ width: `${Math.max(0, Math.min(100, summary[key] * 100))}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function MiniSimilarity({ summary }: { summary?: AdminSimilaritySummary }) {
  const s = summary ?? emptySimilarity();
  const max = Math.max(...sourceKeys.map((key) => s[key]));
  return <span className="sa-admin-mini-sim">{formatSimilarity(max)}</span>;
}

function Pager({ page, pages, total, onPage }: { page: number; pages: number; total: number; onPage: (page: number) => void }) {
  return (
    <div className="sa-admin-pager sa-sans">
      <span>{formatNumber(total)} rows</span>
      <button type="button" className="sa-btn sa-btn--ghost" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span>{page} / {pages}</span>
      <button type="button" className="sa-btn sa-btn--ghost" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
}

function DailyRequestChart({ points }: { points: AdminDailyRequestPoint[] }) {
  const max = Math.max(1, ...points.map((p) => p.total));
  return (
    <svg className="sa-admin-chart" viewBox="0 0 720 260" role="img" aria-label="Daily requests">
      {points.map((point, index) => {
        const width = 640 / Math.max(1, points.length);
        const x = 48 + index * width;
        const registeredHeight = (point.registered / max) * 180;
        const guestHeight = (point.guest / max) * 180;
        return (
          <g key={point.day}>
            <rect x={x} y={220 - registeredHeight} width={Math.max(4, width - 5)} height={registeredHeight} fill="#0B1E3F" />
            <rect x={x} y={220 - registeredHeight - guestHeight} width={Math.max(4, width - 5)} height={guestHeight} fill="#B9B3A0" />
            {index % Math.ceil(Math.max(1, points.length / 6)) === 0 ? <text x={x} y="246">{point.day.slice(5)}</text> : null}
          </g>
        );
      })}
    </svg>
  );
}

function SimilarityLineChart({ points }: { points: AdminDailySimilarityPoint[] }) {
  const series = sourceKeys.map((source, idx) => ({
    source,
    color: ["#0B1E3F", "#2D6A4F", "#8A5A2B", "#7A3E55"][idx],
    d: linePath(points.map((p) => p.avg_similarity?.[source] ?? 0)),
  }));
  return (
    <div>
      <svg className="sa-admin-chart" viewBox="0 0 720 260" role="img" aria-label="Average similarity by source">
        {series.map((item) => <path key={item.source} d={item.d} fill="none" stroke={item.color} strokeWidth="3" />)}
        {points.map((point, index) => (
          index % Math.ceil(Math.max(1, points.length / 6)) === 0 ? <text key={point.day} x={48 + index * (640 / Math.max(1, points.length - 1))} y="246">{point.day.slice(5)}</text> : null
        ))}
      </svg>
      <div className="sa-admin-legend">
        {series.map((item) => <span key={item.source} style={{ color: item.color }}>{sourceLabel(item.source)}</span>)}
      </div>
    </div>
  );
}

function linePath(values: number[]) {
  if (values.length === 0) return "";
  const maxIndex = Math.max(1, values.length - 1);
  return values.map((value, index) => {
    const x = 48 + index * (640 / maxIndex);
    const y = 220 - Math.max(0, Math.min(1, value)) * 180;
    return `${index === 0 ? "M" : "L"} ${x} ${y}`;
  }).join(" ");
}

function buildFilters(raw: {
  since: string;
  until: string;
  user: string;
  audience: string;
  search: string;
  lowSimilarityThreshold: string;
}): AdminQueryFilters {
  const threshold = Number(raw.lowSimilarityThreshold);
  return {
    since: dateToUnix(raw.since),
    until: dateToUnix(raw.until, true),
    user: raw.user.trim() || undefined,
    audience: raw.audience === "registered" || raw.audience === "guest" ? raw.audience : "all",
    search: raw.search.trim() || undefined,
    low_similarity_threshold: Number.isFinite(threshold) && threshold > 0 ? threshold : undefined,
  };
}

function emptySimilarity(): AdminSimilaritySummary {
  return { quran: 0, messenger: 0, scholarly: 0, faq: 0 };
}

function dateInputDaysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function dateToUnix(raw: string, endOfDay = false) {
  if (!raw) return undefined;
  const d = new Date(`${raw}T${endOfDay ? "23:59:59" : "00:00:00"}`);
  return Math.floor(d.getTime() / 1000);
}

function formatUnix(raw: number) {
  if (!raw) return "None";
  return new Date(raw * 1000).toLocaleString();
}

function formatNumber(n: number) {
  return new Intl.NumberFormat().format(n);
}

function formatSimilarity(n: number) {
  return n ? n.toFixed(3) : "0.000";
}

function sourceLabel(source: keyof AdminSimilaritySummary) {
  return source === "faq" ? "FAQ" : source[0].toUpperCase() + source.slice(1);
}

function truncate(value: string, max: number) {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}...`;
}

function tabLabel(tab: AdminTab) {
  return tab[0].toUpperCase() + tab.slice(1);
}

function adminErrorMessage(e: unknown) {
  if (e instanceof ApiError && e.status === 403) {
    return "This account is not marked as an admin.";
  }
  if (e instanceof Error) return e.message;
  return "Something went wrong.";
}

function downloadText(filename: string, contentType: string, content: string) {
  const blob = new Blob([content], { type: contentType || "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function signOut(clearSession: () => void, navigate: (to: string, options?: { replace?: boolean }) => void) {
  clearSession();
  navigate("/", { replace: true });
}
